import React from 'react';
import PropTypes from 'prop-types';
import { Search } from 'semantic-ui-react';
import { fetchAuthenticatedJSON, apiErrorMessage } from 'utils/request';
import { urlParams } from 'utils/request';
import { starfishMediaType } from 'utils/types';
import { throttle } from 'lodash';
import css from './SearchBarContainer.scss';
import ResultRenderer from './ResultRenderer';
import SearchOptionPlaceholder from './SearchOptionPlaceholder';
import { MIN_CHARACTERS, formatData } from 'utils/search';

export default class SearchBarContainer extends React.Component {

  constructor() {
    super();
    this.state = { isLoading: false };
    this.performSearch = throttle(this.performSearch.bind(this), 250);
  }

  componentWillUnmount() {
    // Avoid memory leak.
    // References:
    // https://stackoverflow.com/questions/43142679/react-is-it-possible-to-prevent-state-update-after-unmount-via-setting-this-se
    // https://reactjs.org/blog/2015/12/16/ismounted-antipattern.html
    this.isUnmounted = true;
  }

  performSearch(query) {
    if (query.length < MIN_CHARACTERS) {
      // If some result was previously fetched, reset to empty array to avoid seeing the previous query
      const { connections, services } = this.props.results;
      if ( connections.length !== 0 || services.length !== 0 ) this.props.onResultsFetched({services: [], connections: []});
      return;
    }

    this.setState({isLoading: true});
    const URL = `../api/starfish/success-network/connections?${urlParams({search: query})}`;
    fetchAuthenticatedJSON(URL, {
      method: 'GET',
      headers: {
        'Accept': starfishMediaType('successnetwork')
      }
    })
    .then(this.props.onResultsFetched)
    .catch(e => this.props.onFailure(apiErrorMessage(e)))
    .then(() => { if ( !this.isUnmounted ) this.setState({isLoading: false}) });
  }

  handleSearchChange(e, { value }) {
    // Update the component value no matter what
    this.props.onSearchChange(value);
    this.performSearch(value)
  }

  handleResultSelect(e, { result }) {
    if ( result.type === 'showAll' ) this.redirectToSearchPage();
    else this.props.history.push(`/${result.type}/${result.id}`);
  }

  get noResultsMessage() {
    // We just want to show the loading placeholder when nothing was fetched before
    const { services, connections } = this.props.results;
    return this.state.isLoading && services.length === 0 && connections.length === 0
      ? <SearchOptionPlaceholder />
      : "No results found.";
  }

  redirectToSearchPage() {
    const URL = `/search?q=${this.props.value}`;
    if ( window.location.hash.split("#")[1] !== URL ) this.props.history.push(URL); // Avoid pushing to same URL
  }

  render() {
    return (
      <Search
        value={this.props.value}
        category
        fluid
        minCharacters={MIN_CHARACTERS}
        loading={this.state.isLoading}
        className={css['search-bar-container']}
        aria-label="Search services and people"
        placeholder="Search services and people"
        onSearchChange={this.handleSearchChange.bind(this)}
        onResultSelect={this.handleResultSelect.bind(this)}
        results={formatData(this.props.results, this.props.isStudent, this.redirectToSearchPage.bind(this))}
        resultRenderer={ResultRenderer}
        categoryRenderer={({results, name}) => results.length === 0 ? null : name}
        noResultsMessage={this.noResultsMessage}
        data-role="srvSearchBar"
      />
    );
  }
}

SearchBarContainer.propTypes = {
  results: PropTypes.object.isRequired,
  value: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onResultsFetched: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  isStudent: PropTypes.bool.isRequired,
  onFailure: PropTypes.func.isRequired
};
